import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames'
import TypeUtil from './typeahead.util.js'
import endsWith from 'lodash/endsWith'
import debounce from 'lodash/debounce'
import getLastWord from './cursor.js'
import {
  Alert,
  Box, Button, Chip, ClickAwayListener, Grid, IconButton, Paper, Slide, Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { dataConstants } from '../../../constants/index.js';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { LoadingButton } from '@mui/lab';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as send from '../../../../lib/analytics/send';

const { SECTOR_OPTIONS, INDUSTRY } = dataConstants;

const METRIC_CATEGORY_MAP = {
  'Valuation': ['PE', 'PS', 'PCF', 'PFCF', 'EV', 'EV.EBITDA', 'MKTEBT'],
  'Profitability': ['ROE', 'ROA', 'NETINC.MGN', 'OPER.MGN', 'EBT.MGN', 'EBITDA.MGN'],
  'Revenue & Earnings': ['REV.TTM', 'NETINC.TTM', 'EBIT.TTM', 'EBITDA.TTM', 'EBT.TTM', 'EPS', 'EPS.TTM', 'PROFIT.GROSS.TTM', 'PROFIT.OPER.TTM', 'COSTREV'],
  'Cash Flow': ['CFOPER', 'CFOPER.TTM', 'CFFIN', 'CFFIN.TTM', 'CFINV', 'CFINV.TTM', 'FCF.TTM', 'FCF.PS.TTM', 'FCF.YIELD'],
  'Balance Sheet': ['ASSETS', 'ASSETS.CUR', 'LIABS', 'LIABS.CUR', 'SEQ', 'DEBT.LT', 'DEBT.EQ', 'DEBT.RAT', 'CASHEQ', 'INVY', 'PPE.NET', 'GWILL', 'CR', 'RETEARN', 'MKTCAP'],
  'Dividends & Buybacks': ['DIV.YIELD', 'DIV.PS.TTM'],
  'Risk': ['DRAWDN', 'RISKLEVEL|5Y'],
};

function getCategoryForOption(opt) {
  if (!opt?.aggCode) return 'Other';
  const code = opt.aggCode;
  for (const [cat, codes] of Object.entries(METRIC_CATEGORY_MAP)) {
    if (codes.some(c => code === c || code.startsWith(c + '|') || code.startsWith(c + '.'))) return cat;
  }
  if (code.includes('PCHG')) return 'Growth';
  if (code.startsWith('PCHG|') || code.startsWith('RETURNS|')) return 'Price Performance';
  if (code.includes('SHR.OUTS')) return 'Dividends & Buybacks';
  return 'Other';
}

function VariableDetail(props) {
  var selected = props.selected;
  var assist = props.assist;
  var lead = selected ? selected.legend : assist?.lead;
  var unit = selected && <span>
    Value in: {selected.unit || "No unit"}
  </span>;
  var description = selected ?
    <Typography variant="subtitle1" sx={{ fontSize: '.875rem', mt: 1 }}>
      {selected.description && selected.shortName
        ? selected.description.split(selected.shortName).map((part, i, arr) => (
          <React.Fragment key={i}>{part}{i < arr.length - 1 && <b>{selected.shortName}</b>}</React.Fragment>
        ))
        : selected.description}
    </Typography>
    : assist?.description;
  var help = selected ? unit : assist?.help;
  return (
    <Paper sx={{ height: '100%', p: 2, background: '#f8f9fb', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '8px', boxShadow: 0 }}>
      <Slide direction="left" in={props.selected} mountOnEnter unmountOnExit sx={{ height: '100% !important', '& .MuiCollapse-vertical': { height: '100% !important', } }}>
        <Box component="div" className="callout callout-info" sx={{ height: '100% !important' }}>
          <Typography className="lead" variant="body1">{lead}</Typography>
          {description}
        </Box>
      </Slide>
    </Paper>)
}

VariableDetail.propTypes = {
  selected: PropTypes?.oneOfType([
    PropTypes?.bool,
    PropTypes?.object
  ]),
  assist: PropTypes?.object?.isRequired
}


export class TextArea extends Component {

  constructor(props, context) {
    super(props, context)
    this.handleKeyDown = TypeUtil.handleKeyDown.bind(this)
    this.handleBlur = TypeUtil.handleBlur.bind(this)
    this.hideMenu = TypeUtil.hideMenu.bind(this)
    this.handleUnmount = TypeUtil.handleUnmount.bind(this)

    this.handleChange = this.handleChange.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
    this.insertThis = this.insertThis.bind(this)

    this.state = {
      options: [],
      lastWord: '',
      cursorPos: 0,
      index: -1,
      hideMenu: true,
      top: 0,
      left: 0
    }
  }

  getOptions() {
    return this.state.options;
  }

  getDisplayVal(item, isExact) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: isExact ? 600 : 400 }}>{item.legend}</Typography>
          {item.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.description}
            </Typography>
          )}
        </Box>
        {item?.aggCode && <Chip size="small" label={item.aggCode} sx={{ fontSize: '.7rem', ml: 2, background: '#ebebeb', color: 'rgb(67, 65, 102)', flexShrink: 0 }} />}
      </Box>);
  }

  getDisplayShortName(item) {
    return item.shortName;
  }

  /**
   * Inserts a replacor string at the position of replacee.
   * * FIX: Added `currentCursorPos` parameter to resolve stale state bugs. React's `setState`
   * is asynchronous, so reading `this.state.cursorPos` immediately after a keystroke
   * inside `handleChange` yields the previous cursor position, breaking replacements.
   * * @param {string} replacor - The text to insert (e.g., ' AND\n').
   * @param {string} replacee - The text to be replaced (e.g., ' and').
   * @param {number} [currentCursorPos] - The fresh cursor position (optional, falls back to state).
   */
  insertThis(replacor, replacee, currentCursorPos) {
    if (!this.input) return;
    var fullVal = this.input.value;

    // Explicitly provided cursorPos takes precedence, preserving typeahead click handling via state fallback
    var pos = currentCursorPos !== undefined ? currentCursorPos : this.state.cursorPos;

    var tillCursor = fullVal.substring(0, pos);
    var insertPos = tillCursor.lastIndexOf(replacee);
    if (insertPos === -1) return;

    var before = fullVal.substring(0, insertPos);
    var after = fullVal.substring(pos);
    var newVal = before + replacor + after;
    var newPos = newVal.length - after.length;

    this.input.value = newVal;
    this.input.selectionStart = newPos;
    this.input.selectionEnd = newPos;
    this.setState({ cursorPos: newPos });
    this.hideMenu();
  }

  fetchOptions(word) {
    const lowercaseWord = word?.toLowerCase();
    const suggestions = this.props.suggestions || [];

    if (!lowercaseWord) {
      this.setState({ options: suggestions.map(s => ({ ...s, _isExact: false })) });
      return;
    }

    const exactAggCodeMatches = suggestions.filter(suggestion =>
      suggestion.aggCode.toLowerCase() === lowercaseWord
    ).map(s => ({ ...s, _isExact: true }));

    const shortNameMatches = suggestions.filter(suggestion =>
      suggestion.shortName.toLowerCase().includes(lowercaseWord) && !exactAggCodeMatches.find(e => e.aggCode === suggestion.aggCode)
    ).map(s => ({ ...s, _isExact: false }));

    const legendMatches = suggestions.filter(suggestion =>
      suggestion.legend.toLowerCase().includes(lowercaseWord) && !exactAggCodeMatches.find(e => e.aggCode === suggestion.aggCode) && !shortNameMatches.find(e => e.aggCode === suggestion.aggCode)
    ).map(s => ({ ...s, _isExact: false }));

    const options = [...exactAggCodeMatches, ...shortNameMatches, ...legendMatches];

    this.setState({ options });
  }

  fetchSectorOptions(word) {
    this.setState({
      options: SECTOR_OPTIONS?.filter((suggestion) => suggestion.legend.toLowerCase().includes(word?.toLowerCase()) || suggestion.shortName.toLowerCase().includes(word?.toLowerCase()))
    });
  }

  fetchIndustryOptions(word) {
    this.setState({
      options: INDUSTRY?.filter((suggestion) => suggestion.legend.toLowerCase().includes(word?.toLowerCase()) || suggestion.shortName.toLowerCase().includes(word?.toLowerCase()))
    });
  }

  updateDropdownPosition(inputElement) {
    const { selectionStart, offsetTop, offsetLeft, offsetWidth, scrollTop } = inputElement;
    const lines = inputElement.value.substr(0, selectionStart).split('\n');
    const lineIndex = lines.length - 1;
    const characterOffset = lines[lineIndex].length;
    const maxCols = Math.floor(offsetWidth / (offsetWidth / inputElement.cols));
    const colsOffset = characterOffset % 99;
    const caretX = Math.min(
      offsetLeft + (colsOffset * (maxCols - 15)),
      (offsetLeft) + (offsetWidth)
    );
    const lineHeight = 20;
    const caretY = offsetTop + (lineIndex + 2) * lineHeight;
    this.setState({ top: caretY, left: caretX });
  };

  /**
   * Handles user keystroke input (debounced).
   * Automatically formats logical operators "and" and "or" as soon as the word is typed.
   */
  handleChange() {
    if (!this.input) return;
    var cursorPos = this.input.selectionStart;
    var fullVal = this.input.value;
    this.props.onTextChange(fullVal);
    this.updateDropdownPosition(this.input);
    var tillCursor = fullVal.substring(0, cursorPos);
    var lastWord = getLastWord(tillCursor).trim();
    var normalized = lastWord.toLowerCase();

    this.setState({
      lastWord: lastWord,
      cursorPos: cursorPos
    });

    if (lastWord === "") {
      this.hideMenu();
    }

    // FIX: Pass fresh `cursorPos` to prevent asynchronous stale state replacement bugs.
    // Enhanced detection to catch words even at the start of the line.
    if (endsWith(normalized, ' and') || normalized === 'and') {
      var replaceeAnd = normalized === 'and' ? lastWord : lastWord.substr(-4);
      this.insertThis(' AND\n', replaceeAnd, cursorPos);
      return;
    }

    if (endsWith(normalized, ' or') || normalized === 'or') {
      var replaceeOr = normalized === 'or' ? lastWord : lastWord.substr(-3);
      this.insertThis(' OR ', replaceeOr, cursorPos);
      return;
    }

    if (lastWord && isNaN(lastWord)) {
      TypeUtil.isSector(tillCursor) ? this.fetchSectorOptions(lastWord) :
        TypeUtil.isIndustry(tillCursor) ? this.fetchIndustryOptions(lastWord) :
          lastWord.length >= 2 ? this.fetchOptions(lastWord) : null
      this.setState({
        hideMenu: false,
        index: 0
      });
    } else {
      this.hideMenu();
    }
  }

  handleSelect(index) {
    var selected = this.state.options[index];
    var displayShortName = this.getDisplayShortName(selected) + ' ';
    this.insertThis(displayShortName, this.state.lastWord);
    this.props.onSelect(selected)
  }

  renderOptions() {
    const options = this.state.options;
    if (!options.length) return null;

    // Group by category
    const grouped = {};
    options.forEach((option, idx) => {
      const cat = getCategoryForOption(option);
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push({ ...option, _origIdx: idx });
    });

    const items = [];
    Object.entries(grouped).forEach(([category, catOptions]) => {
      items.push(
        <li key={`header-${category}`}
          style={{ pointerEvents: 'none', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', padding: '8px 16px 4px', borderTop: items.length > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
          {category}
        </li>
      );
      catOptions.forEach((option) => {
        items.push(
          <li
            key={option._origIdx}
            onClick={() => this.handleSelect(option._origIdx)}
            className={option._origIdx === this.state.index ? 'active' : ''}
            style={{ cursor: 'pointer' }}
          >
            <a>{this.getDisplayVal(option, option._isExact)}</a>
          </li>
        );
      });
    });
    return items;
  }

  render() {
    var opLen = this.state.options.length;
    var queryError = this.props.error && <span className="help-block">
      {this.props.error}
    </span>;
    var classes = classNames('subnav dropdown', {
      'open': opLen > 0 && !this.state.hideMenu,
      'has-error': queryError
    });

    return <>
      <Box
        component="div"
        className={classes}
        sx={{
          '&.subnav.dropdown': { position: 'relative' },
          '&.subnav.dropdown .dropdown-menu': { display: 'none' },
          '&.subnav.dropdown.open .dropdown-menu': { display: 'block' },
          '& .dropdown-menu': {
            top: Math.min(this.state.top, 260), left: Math.min(this.state.left, 400),
            position: 'absolute', background: '#fff',
            zIndex: 99, borderRadius: '10px', maxHeight: 320, minWidth: 320, maxWidth: 'calc(100vw - 32px)', overflow: 'auto',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
          },
          '& .dropdown-menu ul': { listStyle: 'none', padding: 0, margin: '5px 0 5px 0' },
          '& .dropdown-menu .active': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
          '& .dropdown-menu li': { pl: 2, pr: 1.4, py: .5, fontSize: '.875rem' },
          '& .dropdown-menu li:hover': { backgroundColor: 'rgb(0 0 0 / 5%)' },
          '& .u-full-width': { minWidth: { md: '100%', sm: '100%', xs: '100%' }, },
          '& textarea:focus': { borderColor: 'rgba(22,27,87,0.4)', boxShadow: '0 0 0 3px rgba(22,27,87,0.08)', outline: 'none' }
        }}
      >
        <textarea
          autoComplete="off"
          spellCheck="false"
          required
          className="form-control u-full-width"
          id="query-builder"
          rows={4}
          ref={el => this.input = el}
          onKeyDown={(event) => {
            !this.state.hideMenu && this.handleKeyDown(event);
            ((event.metaKey || event.ctrlKey) && event.key === 'Enter') ? this?.props?.onKeyDown() : null
          }}
          onKeyPress={this.handleEnterPress}
          onChange={debounce(this.handleChange, 120)}
          placeholder={this.props.placeholder}
          defaultValue={this.props.value}
          name={this.props.name}
          aria-label="Stock screener query"
          style={{
            fontFamily: "'SF Mono', 'Consolas', 'Monaco', 'Menlo', monospace",
            borderRadius: '8px',
            padding: '10px 14px',
            lineHeight: 1.5,
            minHeight: '45px',
            border: '1px solid rgba(0,0,0,0.12)',
            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
            color: 'hsl(240, 16%, 16%)',
            fontSize: '.85rem',
            background: '#f8f9fb',
            resize: 'vertical',
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        {this.input?.value !== "" &&
          <IconButton size="small" aria-label="Clear query" sx={{ position: 'absolute', top: 5, right: 5, opacity: 0.5, '&:hover': { opacity: 1 } }} onClick={() => { if (this.input) { this.input.value = ""; this.props.onClearText(); } }}>
            <ClearRoundedIcon fontSize="small" />
          </IconButton>}
        <ClickAwayListener
          mouseEvent="onMouseDown"
          touchEvent="onTouchStart"
          onClickAway={(e) => this.hideMenu()}
        >
          <div className="dropdown-menu">
            <ul>
              {this.renderOptions()}
            </ul>
          </div>
        </ClickAwayListener>
        {(() => {
          const text = this.input?.value || this.props.value || '';
          if (!text.trim()) return null;
          const statuses = TypeUtil.validateLines(text, this.props.suggestions || []);
          const errors = statuses.filter(s => s.status === 'error');
          const validCount = statuses.filter(s => s.status === 'valid').length;
          if (errors.length === 0 && validCount > 0) {
            return (
              <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.5, ml: 0.5 }}>
                ✓ Query looks valid
              </Typography>
            );
          }
          if (errors.length > 0) {
            return (
              <Box sx={{ mt: 0.5, ml: 0.5 }}>
                {errors.map(e => (
                  <Typography key={e.lineIndex} variant="caption" color="error" sx={{ display: 'block' }}>
                    Line {e.lineIndex + 1}: {e.message}
                  </Typography>
                ))}
              </Box>
            );
          }
          return null;
        })()}
      </Box>
      {queryError}
    </>
  }
}

TextArea.propTypes = {
  name: PropTypes?.string.isRequired,
  placeholder: PropTypes?.string.isRequired,
  value: PropTypes?.string,
  onSelect: PropTypes?.func.isRequired,
  onTextChange: PropTypes?.func.isRequired,
  onClearText: PropTypes?.func.isRequired,
  error: PropTypes?.oneOfType([
    PropTypes?.bool,
    PropTypes?.string
  ])
}

class QueryBuilder extends Component {
  constructor(props, context) {
    super(props, context)
    this.handleInsert = this.handleInsert.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
    this.state = {
      selected: false,
      tooltip: false,
      inputString: this.props.value
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ inputString: this.props.value }, () => {
        this.updateTextareaValue();
      });
    }
  }

  updateTextareaValue() {
    const { inputString } = this.state;
    const textarea = document.getElementById('query-builder');
    if (textarea) {
      textarea.value = inputString;
    }
  }

  handleInsert(ratio) {
    if (ratio.constructor === Array) {
      var selected = {
        unit: ratio[0],
        name: ratio[1],
        description: ratio[2]
      }
      this.textarea.insertThis(selected.name + " ", "")
      this.setState({ selected: selected })
    } else {
      this.textarea.insertThis(ratio + " ", "")
    }
  }

  handleSelect(selected) {
    this.setState({ selected: selected })
  }

  handleInputText(inputValue) {
    this.setState({ inputValue: inputValue })
  }

  handleQuery(options) {
    if (this.state.inputString !== "" && this.state.inputString[0] !== "\n") {
      const _options = options.sort((a, b) => b.shortName.length - a.shortName.length);
      var convertedString = this?.textarea?.input?.value ?? this.state.inputString;
      for (var i = 0; i < _options.length; i++) {
        var entry = _options[i];
        var shortName = entry.shortName;
        var aggCode = entry.aggCode;
        var regex = new RegExp("\\b" + shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + "\\b", "gi");
        convertedString = convertedString.replace(regex, "`" + aggCode + "`");
      }
      this.setState({ selected: false })
      this.props?.setFormula(TypeUtil.getFormula(convertedString, _options));
      this.props?.handleNewMetrics(TypeUtil.getFormula(convertedString, _options))
    } else {
      this.setState({ tooltip: true })
    }
  }

  render() {
    return <>
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1, width: '25ch' },
        }}
        autoComplete="off"
        onSubmit={(event) => { event.preventDefault(); this.handleQuery(this.props.options) }}
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="stretch" spacing={2}
        >
          {this.props.matchedResults && this.state.inputString !== "" &&
            <Grid size={{ md: 2, sm: 12, xs: 12 }} sx={{ display: { xs: 'block', md: 'none', sm: 'block' }, mb: .7 }}>
              {this.props.matchedResults}
            </Grid>}
          <Grid size={{ md: this.state.selected && this.state.inputString !== "" ? 7 : this.props.screenerNotFound ? 6 : 10, sm: 12, xs: 12 }}>
            <>
              <TextArea
                name={this.props.name}
                placeholder={this.props.placeholder}
                value={this.props.presetCriteria ?? this.state.inputString}
                onSelect={this.handleSelect}
                onKeyDown={() => this.handleQuery(this.props.options)}
                onTextChange={(inputString) => this.setState({ inputString: inputString })}
                onClearText={() => this.setState({ inputString: "" })}
                ref={el => this.textarea = el}
                suggestions={this.props.options}
                error={this.props.errorMessage && !this.props.estimatedResults &&
                  <Alert sx={{ maxWidth: '100%', my: 1, }} severity="error">Error – {this.props.errorMessage}</Alert>}
              />
              {this.props.screenerNotFound &&
                <Button size="small"
                  data-event-name="guide-link" data-event-category="screener" data-event-label="screener-guide"
                  sx={{ textAlign: 'left', textTransform: "none", color: '#174ea6' }}
                  href='/stock-screener/guide'
                  startIcon={<InfoOutlinedIcon />}
                  onClick={(e) => send.uevent(e)}
                >
                  View detailed guide for creating screens
                </Button>}
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <LoadingButton
                  data-event-name="run-screen" data-event-category="stock-screener" data-event-label={this.props.presetCriteria ?? this.state.inputString}
                  variant="contained" type="submit"
                  loading={this?.props?.isLoading}
                  loadingPosition="end"
                  sx={{
                    textTransform: "none", fontSize: '.84rem', fontWeight: 600,
                    borderRadius: '6px', px: 2.5,
                    bgcolor: '#161b57', '&:hover': { bgcolor: '#2a2d64' },
                  }}
                  onClick={(e) => { this.handleQuery(this.props.options); send.uevent(e) }}
                  endIcon={<PlayArrowRoundedIcon />}
                  size="small"
                  disableElevation
                >
                  Run Screen
                </LoadingButton>
                {this?.props?.saveScreener}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', display: { xs: 'none', md: 'block' }, userSelect: 'none' }}>
                  ⌘+Enter to run
                </Typography>
              </Box>
            </>
          </Grid>
          {this.props.screenerNotFound &&
            <Grid size={{ md: 4, sm: 12, xs: 12 }} sx={{ display: { xs: 'block', md: 'block', sm: 'block' }, mb: .7 }}>
              {this.props.queryExamples}
            </Grid>}
          {this.state.selected && this.state.inputString !== "" &&
            <Grid size={{ md: 3, sm: 12, xs: 12 }}>
              <VariableDetail
                selected={this.state.selected}
              />
            </Grid>}
        </Grid>
      </Box>
    </>
  }
}

QueryBuilder.propTypes = {
  name: PropTypes?.string.isRequired,
  suggestions: PropTypes?.array.isRequired,
  placeholder: PropTypes?.string.isRequired,
  value: PropTypes?.string,
  assist: PropTypes?.object.isRequired,
  error: PropTypes?.oneOfType([
    PropTypes?.bool,
    PropTypes?.string
  ])
}

export default QueryBuilder

// import React, { Component } from 'react';
// import PropTypes from 'prop-types';
// import classNames from 'classnames'
// import TypeUtil from './typeahead.util.js'
// import endsWith from 'lodash/endsWith'
// import debounce from 'lodash/debounce'
// import getLastWord from './cursor.js'
// import {
//   Alert,
//   Box, Button, Chip, ClickAwayListener, Grid, IconButton, Paper, Slide, Typography,
//   // ListItemIcon, ListItemText, MenuItem,
//   // Collapse, MenuList, Tooltip
// } from '@mui/material';
// import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
// import { dataConstants } from '../../../constants/index.js';
// import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
// import { LoadingButton } from '@mui/lab';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import * as send from '../../../../lib/analytics/send';

// const { SECTOR_OPTIONS, INDUSTRY } = dataConstants;

// const METRIC_CATEGORY_MAP = {
//   'Valuation': ['PE', 'PS', 'PCF', 'PFCF', 'EV', 'EV.EBITDA', 'MKTEBT'],
//   'Profitability': ['ROE', 'ROA', 'NETINC.MGN', 'OPER.MGN', 'EBT.MGN', 'EBITDA.MGN'],
//   'Revenue & Earnings': ['REV.TTM', 'NETINC.TTM', 'EBIT.TTM', 'EBITDA.TTM', 'EBT.TTM', 'EPS', 'EPS.TTM', 'PROFIT.GROSS.TTM', 'PROFIT.OPER.TTM', 'COSTREV'],
//   'Cash Flow': ['CFOPER', 'CFOPER.TTM', 'CFFIN', 'CFFIN.TTM', 'CFINV', 'CFINV.TTM', 'FCF.TTM', 'FCF.PS.TTM', 'FCF.YIELD'],
//   'Balance Sheet': ['ASSETS', 'ASSETS.CUR', 'LIABS', 'LIABS.CUR', 'SEQ', 'DEBT.LT', 'DEBT.EQ', 'DEBT.RAT', 'CASHEQ', 'INVY', 'PPE.NET', 'GWILL', 'CR', 'RETEARN', 'MKTCAP'],
//   'Dividends & Buybacks': ['DIV.YIELD', 'DIV.PS.TTM'],
//   'Risk': ['DRAWDN', 'RISKLEVEL|5Y'],
// };

// function getCategoryForOption(opt) {
//   if (!opt?.aggCode) return 'Other';
//   const code = opt.aggCode;
//   for (const [cat, codes] of Object.entries(METRIC_CATEGORY_MAP)) {
//     if (codes.some(c => code === c || code.startsWith(c + '|') || code.startsWith(c + '.'))) return cat;
//   }
//   if (code.includes('PCHG')) return 'Growth';
//   if (code.startsWith('PCHG|') || code.startsWith('RETURNS|')) return 'Price Performance';
//   if (code.includes('SHR.OUTS')) return 'Dividends & Buybacks';
//   return 'Other';
// }

// function VariableDetail(props) {
//   var selected = props.selected;
//   var assist = props.assist;
//   var lead = selected ? selected.legend : assist?.lead;
//   var unit = selected && <span>
//     Value in: {selected.unit || "No unit"}
//   </span>;
//   var description = selected ?
//     <Typography variant="subtitle1" sx={{ fontSize: '.875rem', mt: 1 }}>
//       {selected.description && selected.shortName
//         ? selected.description.split(selected.shortName).map((part, i, arr) => (
//           <React.Fragment key={i}>{part}{i < arr.length - 1 && <b>{selected.shortName}</b>}</React.Fragment>
//         ))
//         : selected.description}
//     </Typography>
//     : assist?.description;
//   var help = selected ? unit : assist?.help;
//   return (
//     <Paper sx={{ height: '100%', p: 2, background: '#f8f9fb', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '8px', boxShadow: 0 }}>
//       <Slide direction="left" in={props.selected} mountOnEnter unmountOnExit sx={{ height: '100% !important', '& .MuiCollapse-vertical': { height: '100% !important', } }}>
//         <Box component="div" className="callout callout-info" sx={{ height: '100% !important' }}>
//           <Typography className="lead" variant="body1">{lead}</Typography>
//           {description}
//           {/* <small>{help}</small> */}
//         </Box>
//       </Slide>
//     </Paper>)
// }

// VariableDetail.propTypes = {
//   selected: PropTypes?.oneOfType([
//     PropTypes?.bool,
//     PropTypes?.object
//   ]),
//   assist: PropTypes?.object?.isRequired
// }


// export class TextArea extends Component {

//   constructor(props, context) {
//     super(props, context)
//     // TypeaheadUtil defaults
//     this.handleKeyDown = TypeUtil.handleKeyDown.bind(this)
//     this.handleBlur = TypeUtil.handleBlur.bind(this)
//     this.hideMenu = TypeUtil.hideMenu.bind(this)
//     this.handleUnmount = TypeUtil.handleUnmount.bind(this)
//     // End TypeaheadUtil defaults
//     this.handleChange = this.handleChange.bind(this)
//     this.handleSelect = this.handleSelect.bind(this)
//     this.insertThis = this.insertThis.bind(this)
//     this.state = {
//       options: [],
//       lastWord: '',
//       cursorPos: 0,
//       index: -1,
//       hideMenu: true,
//       top: 0,
//       left: 0
//     }
//   }

//   getOptions() {
//     return this.state.options;
//   }

//   getDisplayVal(item, isExact) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
//         <Box sx={{ minWidth: 0, flex: 1 }}>
//           <Typography variant="body2" sx={{ fontWeight: isExact ? 600 : 400 }}>{item.legend}</Typography>
//           {item.description && (
//             <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//               {item.description}
//             </Typography>
//           )}
//         </Box>
//         {item?.aggCode && <Chip size="small" label={item.aggCode} sx={{ fontSize: '.7rem', ml: 2, background: '#ebebeb', color: 'rgb(67, 65, 102)', flexShrink: 0 }} />}
//       </Box>);
//   }

//   getDisplayShortName(item) {
//     return item.shortName;
//   }

//   insertThis(replacor, replacee) {
//     if (!this.input) return;
//     var fullVal = this.input.value;
//     var tillCursor = fullVal.substring(0, this.state.cursorPos);
//     var insertPos = tillCursor.lastIndexOf(replacee);
//     if (insertPos == -1)
//       return;
//     var before = fullVal.substring(0, insertPos);
//     var after = fullVal.substring(this.state.cursorPos);
//     var newVal = before + replacor + after;
//     var newPos = newVal.length - after.length;
//     this.input.value = newVal;
//     this.input.selectionStart = newPos;
//     this.input.selectionEnd = newPos;
//     this.setState({ cursorPos: newPos });
//     this.hideMenu();
//   }

//   // fetchOptions(word) {
//   //   this.setState({
//   //     options: this.props.suggestions?.filter((suggestion) => suggestion.legend.toLowerCase().includes(word?.toLowerCase()) || suggestion.shortName.toLowerCase().includes(word?.toLowerCase()))
//   //   });
//   // }

//   fetchOptions(word) {
//     const lowercaseWord = word?.toLowerCase();
//     const suggestions = this.props.suggestions || [];

//     if (!lowercaseWord) {
//       this.setState({ options: suggestions.map(s => ({ ...s, _isExact: false })) });
//       return;
//     }

//     const exactAggCodeMatches = suggestions.filter(suggestion =>
//       suggestion.aggCode.toLowerCase() === lowercaseWord
//     ).map(s => ({ ...s, _isExact: true }));

//     const shortNameMatches = suggestions.filter(suggestion =>
//       suggestion.shortName.toLowerCase().includes(lowercaseWord) && !exactAggCodeMatches.find(e => e.aggCode === suggestion.aggCode)
//     ).map(s => ({ ...s, _isExact: false }));

//     const legendMatches = suggestions.filter(suggestion =>
//       suggestion.legend.toLowerCase().includes(lowercaseWord) && !exactAggCodeMatches.find(e => e.aggCode === suggestion.aggCode) && !shortNameMatches.find(e => e.aggCode === suggestion.aggCode)
//     ).map(s => ({ ...s, _isExact: false }));

//     const options = [...exactAggCodeMatches, ...shortNameMatches, ...legendMatches];

//     this.setState({ options });
//   }

//   fetchSectorOptions(word) {
//     this.setState({
//       options: SECTOR_OPTIONS?.filter((suggestion) => suggestion.legend.toLowerCase().includes(word?.toLowerCase()) || suggestion.shortName.toLowerCase().includes(word?.toLowerCase()))
//     });
//   }

//   fetchIndustryOptions(word) {
//     this.setState({
//       options: INDUSTRY?.filter((suggestion) => suggestion.legend.toLowerCase().includes(word?.toLowerCase()) || suggestion.shortName.toLowerCase().includes(word?.toLowerCase()))
//     });
//   }

//   updateDropdownPosition(inputElement) {
//     const { selectionStart, offsetTop, offsetLeft, offsetWidth, scrollTop } = inputElement;
//     const lines = inputElement.value.substr(0, selectionStart).split('\n');
//     const lineIndex = lines.length - 1;
//     const characterOffset = lines[lineIndex].length;
//     const maxCols = Math.floor(offsetWidth / (offsetWidth / inputElement.cols));
//     const colsOffset = characterOffset % 99;
//     const caretX = Math.min(
//       offsetLeft + (colsOffset * (maxCols - 15)),
//       (offsetLeft) + (offsetWidth)
//     );
//     const lineHeight = 20;
//     const caretY = offsetTop + (lineIndex + 2) * lineHeight;
//     this.setState({ top: caretY, left: caretX });
//   };

//   handleChange() {
//     if (!this.input) return;
//     var cursorPos = this.input.selectionStart;
//     var fullVal = this.input.value;
//     this.props.onTextChange(fullVal);
//     this.updateDropdownPosition(this.input);
//     var tillCursor = fullVal.substring(0, cursorPos);
//     var lastWord = getLastWord(tillCursor).trim();
//     var normalized = lastWord.toLowerCase();
//     this.setState({
//       lastWord: lastWord,
//       cursorPos: cursorPos
//     });
//     if (lastWord === "") {
//       this.hideMenu();
//     }
//     if (endsWith(normalized, ' and')) {
//       this.insertThis(' AND\n', lastWord.substr(-4));
//       return;
//     }
//     if (endsWith(normalized, ' or')) {
//       this.insertThis(' OR ', lastWord.substr(-3));
//       return;
//     }
//     if (lastWord && isNaN(lastWord)) {
//       TypeUtil.isSector(tillCursor) ? this.fetchSectorOptions(lastWord) :
//         TypeUtil.isIndustry(tillCursor) ? this.fetchIndustryOptions(lastWord) :
//           lastWord.length >= 2 ? this.fetchOptions(lastWord) : null
//       this.setState({
//         hideMenu: false,
//         index: 0
//       });
//     } else {
//       this.hideMenu();
//     }
//   }

//   // handleChange() {
//   //   var cursorPos = this.refs.input.selectionStart;
//   //   var fullVal = this.refs.input.value;
//   //   this.props.onTextChange(fullVal)
//   //   this.updateDropdownPosition(this.refs?.input)
//   //   var tillCursor = fullVal.substring(0, cursorPos);
//   //   var lastWord = getLastWord(tillCursor).trim();
//   //   var normalized = lastWord.toLowerCase();
//   //   this.setState({
//   //     lastWord: lastWord,
//   //     cursorPos: cursorPos
//   //   });
//   //   if (lastWord === "") {
//   //     this.hideMenu();
//   //   }
//   //   if (endsWith(normalized, ' and')) {
//   //     this.insertThis(' AND\n', lastWord.substr(-4));
//   //     return;
//   //   }
//   //   if (endsWith(normalized, ' or')) {
//   //     this.insertThis(' OR ', lastWord.substr(-3));
//   //     return;
//   //   }
//   //   if (lastWord.length >= 2 && isNaN(lastWord)) {
//   //     this.fetchOptions(lastWord);
//   //     this.setState({
//   //       hideMenu: false,
//   //       index: 0
//   //     });
//   //   } else {
//   //     this.hideMenu();
//   //   }
//   // }

//   handleSelect(index) {
//     var selected = this.state.options[index];
//     // var displayVal = this.getDisplayVal(selected) + ' ';
//     // this.insertThis(displayVal, this.state.lastWord);
//     var displayShortName = this.getDisplayShortName(selected) + ' ';
//     this.insertThis(displayShortName, this.state.lastWord);
//     this.props.onSelect(selected)
//   }

//   renderOptions() {
//     const options = this.state.options;
//     if (!options.length) return null;

//     // Group by category
//     const grouped = {};
//     options.forEach((option, idx) => {
//       const cat = getCategoryForOption(option);
//       if (!grouped[cat]) grouped[cat] = [];
//       grouped[cat].push({ ...option, _origIdx: idx });
//     });

//     const items = [];
//     Object.entries(grouped).forEach(([category, catOptions]) => {
//       items.push(
//         <li key={`header-${category}`}
//           style={{ pointerEvents: 'none', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', padding: '8px 16px 4px', borderTop: items.length > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
//           {category}
//         </li>
//       );
//       catOptions.forEach((option) => {
//         items.push(
//           <li
//             key={option._origIdx}
//             onClick={() => this.handleSelect(option._origIdx)}
//             className={option._origIdx === this.state.index ? 'active' : ''}
//             style={{ cursor: 'pointer' }}
//           >
//             <a>{this.getDisplayVal(option, option._isExact)}</a>
//           </li>
//         );
//       });
//     });
//     return items;
//   }

//   render() {
//     var opLen = this.state.options.length;
//     var queryError = this.props.error && <span className="help-block">
//       {this.props.error}
//     </span>;
//     var classes = classNames('subnav dropdown', {
//       'open': opLen > 0 && !this.state.hideMenu,
//       'has-error': queryError
//     });

//     return <>
//       <Box
//         component="div"
//         className={classes}
//         sx={{
//           '&.subnav.dropdown': { position: 'relative' },
//           '&.subnav.dropdown .dropdown-menu': { display: 'none' },
//           '&.subnav.dropdown.open .dropdown-menu': { display: 'block' },
//           '& .dropdown-menu': {
//             top: Math.min(this.state.top, 260), left: Math.min(this.state.left, 400),
//             position: 'absolute', background: '#fff',
//             zIndex: 99, borderRadius: '10px', maxHeight: 320, minWidth: 320, maxWidth: 'calc(100vw - 32px)', overflow: 'auto',
//             boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
//             border: '1px solid rgba(0,0,0,0.06)',
//           },
//           '& .dropdown-menu ul': { listStyle: 'none', padding: 0, margin: '5px 0 5px 0' },
//           '& .dropdown-menu .active': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
//           '& .dropdown-menu li': { pl: 2, pr: 1.4, py: .5, fontSize: '.875rem' },
//           '& .dropdown-menu li:hover': { backgroundColor: 'rgb(0 0 0 / 5%)' },
//           '& .u-full-width': { minWidth: { md: '100%', sm: '100%', xs: '100%' }, },
//           '& textarea:focus': { borderColor: 'rgba(22,27,87,0.4)', boxShadow: '0 0 0 3px rgba(22,27,87,0.08)', outline: 'none' }
//         }}
//       >
//         <textarea
//           autoComplete="off"
//           spellCheck="false"
//           required
//           className="form-control u-full-width"
//           id="query-builder"
//           // maxlength="1500"
//           rows={4}
//           ref={el => this.input = el}
//           onKeyDown={(event) => {
//             !this.state.hideMenu && this.handleKeyDown(event);
//             ((event.metaKey || event.ctrlKey) && event.key === 'Enter') ? this?.props?.onKeyDown() : null
//           }}
//           onKeyPress={this.handleEnterPress}
//           onChange={debounce(this.handleChange, 120)}
//           // onBlur={this.handleBlur}
//           placeholder={this.props.placeholder}
//           defaultValue={this.props.value}
//           name={this.props.name}
//           aria-label="Stock screener query"
//           style={{
//             fontFamily: "'SF Mono', 'Consolas', 'Monaco', 'Menlo', monospace",
//             borderRadius: '8px',
//             padding: '10px 14px',
//             lineHeight: 1.5,
//             minHeight: '45px',
//             border: '1px solid rgba(0,0,0,0.12)',
//             transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
//             color: 'hsl(240, 16%, 16%)',
//             fontSize: '.85rem',
//             background: '#f8f9fb',
//             resize: 'vertical',
//             width: '100%',
//             boxSizing: 'border-box',
//           }}
//         />
//         {this.input?.value !== "" &&
//           <IconButton size="small" aria-label="Clear query" sx={{ position: 'absolute', top: 5, right: 5, opacity: 0.5, '&:hover': { opacity: 1 } }} onClick={() => { if (this.input) { this.input.value = ""; this.props.onClearText(); } }}>
//             <ClearRoundedIcon fontSize="small" />
//           </IconButton>}
//         <ClickAwayListener
//           mouseEvent="onMouseDown"
//           touchEvent="onTouchStart"
//           onClickAway={(e) => this.hideMenu()}
//         >
//           <div className="dropdown-menu">
//             <ul>
//               {this.renderOptions()}
//             </ul>
//           </div>
//         </ClickAwayListener>
//         {(() => {
//           const text = this.input?.value || this.props.value || '';
//           if (!text.trim()) return null;
//           const statuses = TypeUtil.validateLines(text, this.props.suggestions || []);
//           const errors = statuses.filter(s => s.status === 'error');
//           const validCount = statuses.filter(s => s.status === 'valid').length;
//           if (errors.length === 0 && validCount > 0) {
//             return (
//               <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.5, ml: 0.5 }}>
//                 ✓ Query looks valid
//               </Typography>
//             );
//           }
//           if (errors.length > 0) {
//             return (
//               <Box sx={{ mt: 0.5, ml: 0.5 }}>
//                 {errors.map(e => (
//                   <Typography key={e.lineIndex} variant="caption" color="error" sx={{ display: 'block' }}>
//                     Line {e.lineIndex + 1}: {e.message}
//                   </Typography>
//                 ))}
//               </Box>
//             );
//           }
//           return null;
//         })()}
//       </Box>
//       {queryError}
//       {/* <Typography variant="caption" color="text.secondary" sx={{ position: 'absolutee', bottom: 57, right: 10, background: 'rgba(255,255,255,0.9)', borderRadius: '25px', pointerEvents: "none", px: .7, pb: 0, pt: .5 }}>
//           ⌘ + Enter
//         </Typography> */}
//     </>
//   }
// }

// TextArea.propTypes = {
//   name: PropTypes?.string.isRequired,
//   placeholder: PropTypes?.string.isRequired,
//   value: PropTypes?.string,
//   onSelect: PropTypes?.func.isRequired,
//   onTextChange: PropTypes?.func.isRequired,
//   onClearText: PropTypes?.func.isRequired,
//   error: PropTypes?.oneOfType([
//     PropTypes?.bool,
//     PropTypes?.string
//   ])
// }


// class QueryBuilder extends Component {
//   constructor(props, context) {
//     super(props, context)
//     this.handleInsert = this.handleInsert.bind(this)
//     this.handleSelect = this.handleSelect.bind(this)
//     this.state = {
//       selected: false,
//       tooltip: false,
//       inputString: this.props.value
//     }
//   }

//   componentDidUpdate(prevProps) {
//     if (prevProps.value !== this.props.value) {
//       this.setState({ inputString: this.props.value }, () => {
//         this.updateTextareaValue();
//       });
//     }
//   }

//   updateTextareaValue() {
//     const { inputString } = this.state;
//     const textarea = document.getElementById('query-builder');
//     if (textarea) {
//       textarea.value = inputString;
//     }
//   }

//   handleInsert(ratio) {
//     if (ratio.constructor === Array) {
//       var selected = {
//         unit: ratio[0],
//         name: ratio[1],
//         description: ratio[2]
//       }
//       this.textarea.insertThis(selected.name + " ", "")
//       this.setState({ selected: selected })
//     } else {
//       this.textarea.insertThis(ratio + " ", "")
//     }
//   }

//   handleSelect(selected) {
//     this.setState({ selected: selected })
//   }

//   handleInputText(inputValue) {
//     this.setState({ inputValue: inputValue })
//   }

//   handleQuery(options) {
//     if (this.state.inputString !== "" && this.state.inputString[0] !== "\n") {
//       const _options = options.sort((a, b) => b.shortName.length - a.shortName.length);
//       var convertedString = this?.textarea?.input?.value ?? this.state.inputString;
//       for (var i = 0; i < _options.length; i++) {
//         var entry = _options[i];
//         var shortName = entry.shortName;
//         var aggCode = entry.aggCode;
//         var regex = new RegExp("\\b" + shortName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + "\\b", "gi");
//         convertedString = convertedString.replace(regex, "`" + aggCode + "`");
//       }
//       this.setState({ selected: false })
//       this.props?.setFormula(TypeUtil.getFormula(convertedString, _options));
//       this.props?.handleNewMetrics(TypeUtil.getFormula(convertedString, _options))
//     } else {
//       this.setState({ tooltip: true })
//     }
//   }

//   render() {
//     return <>
//       <Box
//         component="form"
//         sx={{
//           '& .MuiTextField-root': { m: 1, width: '25ch' },
//         }}
//         // noValidate
//         autoComplete="off"
//         onSubmit={(event) => { event.preventDefault(); this.handleQuery(this.props.options) }}
//       >
//         <Grid
//           container
//           direction="row"
//           justifyContent="space-between"
//           alignItems="stretch" spacing={2}
//         >
//           {this.props.matchedResults && this.state.inputString !== "" &&
//             <Grid size={{ md: 2, sm: 12, xs: 12 }} sx={{ display: { xs: 'block', md: 'none', sm: 'block' }, mb: .7 }}>
//               {this.props.matchedResults}
//             </Grid>}
//           <Grid size={{ md: this.state.selected && this.state.inputString !== "" ? 7 : this.props.screenerNotFound ? 6 : 10, sm: 12, xs: 12 }}>
//             <>
//               <TextArea
//                 name={this.props.name}
//                 placeholder={this.props.placeholder}
//                 value={this.props.presetCriteria ?? this.state.inputString}
//                 onSelect={this.handleSelect}
//                 onKeyDown={() => this.handleQuery(this.props.options)}
//                 onTextChange={(inputString) => this.setState({ inputString: inputString })}
//                 onClearText={() => this.setState({ inputString: "" })}
//                 ref={el => this.textarea = el}
//                 suggestions={this.props.options}
//                 error={this.props.errorMessage && !this.props.estimatedResults &&
//                   <Alert sx={{ maxWidth: '100%', my: 1, }} severity="error">Error – {this.props.errorMessage}</Alert>}
//               />
//               {this.props.screenerNotFound &&
//                 <Button size="small"
//                   data-event-name="guide-link" data-event-category="screener" data-event-label="screener-guide"
//                   sx={{ textAlign: 'left', textTransform: "none", color: '#174ea6' }}
//                   href='/stock-screener/guide'
//                   startIcon={<InfoOutlinedIcon />}
//                   onClick={(e) => send.uevent(e)}
//                 >
//                   View detailed guide for creating screens
//                 </Button>}
//               <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
//                 <LoadingButton
//                   data-event-name="run-screen" data-event-category="stock-screener" data-event-label={this.props.presetCriteria ?? this.state.inputString}
//                   variant="contained" type="submit"
//                   loading={this?.props?.isLoading}
//                   loadingPosition="end"
//                   sx={{
//                     textTransform: "none", fontSize: '.84rem', fontWeight: 600,
//                     borderRadius: '6px', px: 2.5,
//                     bgcolor: '#161b57', '&:hover': { bgcolor: '#2a2d64' },
//                   }}
//                   onClick={(e) => { this.handleQuery(this.props.options); send.uevent(e) }}
//                   endIcon={<PlayArrowRoundedIcon />}
//                   size="small"
//                   disableElevation
//                 >
//                   Run Screen
//                 </LoadingButton>
//                 {this?.props?.saveScreener}
//                 <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', display: { xs: 'none', md: 'block' }, userSelect: 'none' }}>
//                   ⌘+Enter to run
//                 </Typography>
//               </Box>
//             </>
//             {/* <Tooltip
//             PopperProps={{
//               disablePortal: true,
//             }}
//             onClose={() => this.setState({ tooltip: false })}
//             open={this.state.tooltip || this.state.inputString === ""}
//             disableFocusListener
//             disableHoverListener
//             disableTouchListener
//             title="Add"
//           >
//             <div />
//           </Tooltip> */}
//           </Grid>
//           {this.props.screenerNotFound &&
//             <Grid size={{ md: 4, sm: 12, xs: 12 }} sx={{ display: { xs: 'block', md: 'block', sm: 'block' }, mb: .7 }}>
//               {this.props.queryExamples}
//             </Grid>}
//           {/* {this.props.matchedResults && this.state.inputString !== "" &&
//             <Grid size={{ md: 2, sm: 12, xs: 12 }} sx={{ display: { xs: 'none', md: 'block', sm: 'none' } }}>
//               {this.props.matchedResults}
//             </Grid>} */}
//           {this.state.selected && this.state.inputString !== "" &&
//             <Grid size={{ md: 3, sm: 12, xs: 12 }}>
//               <VariableDetail
//                 // assist={this.props.assist}
//                 selected={this.state.selected}
//               />
//             </Grid>}
//           {/* <Grid size={{ md: 12, sm: 12, xs: 12 }}>
//             <LoadingButton
//               // component="a"
//               variant="contained" type="submit"
//               loading={this?.props?.isLoading}
//               loadingPosition="end"
//               sx={{
//                 textTransform: "none", fontSize: '.875rem', fontWeight: 600,
//                 mt: { md: 0, sm: 1.5, xs: 1 }, textAlign: { xs: 'right !important', sm: 'right !important' }
//               }}
//               onClick={() => this.handleQuery(this.props.options)}
//               endIcon={<PlayArrowRoundedIcon />}
//               size="small"
//               disableElevation
//             // href="#build-query"
//             >
//               Run
//             </LoadingButton>
//             {this?.props?.saveScreener}
//           </Grid> */}
//         </Grid>
//       </Box>
//     </>
//   }
// }

// QueryBuilder.propTypes = {
//   name: PropTypes?.string.isRequired,
//   suggestions: PropTypes?.array.isRequired,
//   placeholder: PropTypes?.string.isRequired,
//   value: PropTypes?.string,
//   assist: PropTypes?.object.isRequired,
//   error: PropTypes?.oneOfType([
//     PropTypes?.bool,
//     PropTypes?.string
//   ])
// }

// export default QueryBuilder






// // fetchOptions(word) {
// //   const lowercaseWord = word?.toLowerCase();

// //   if (!lowercaseWord) {
// //     // If the search word is empty or undefined, return all suggestions
// //     this.setState({
// //       options: this.props.suggestions || []
// //     });
// //   } else {
// //     const suggestions = this.props.suggestions || [];

// //     // Filter for items that start with the search term
// //     const startsWithSearchTerm = suggestions.filter(suggestion => {
// //       const lowercaseLegend = suggestion.legend.toLowerCase();
// //       const lowercaseShortName = suggestion.shortName.toLowerCase();
// //       return (
// //         lowercaseLegend.startsWith(lowercaseWord) ||
// //         lowercaseShortName.startsWith(lowercaseWord)
// //       );
// //     });

// //     // Filter for partial matches (excluding those that start with the search term)
// //     const partialMatches = suggestions.filter(suggestion => {
// //       const lowercaseLegend = suggestion.legend.toLowerCase();
// //       const lowercaseShortName = suggestion.shortName.toLowerCase();
// //       return (
// //         (lowercaseLegend.includes(lowercaseWord) ||
// //           lowercaseShortName.includes(lowercaseWord)
// //         ) && !startsWithSearchTerm.includes(suggestion))
// //     });

// //     // Concatenate items that start with the search term at the beginning
// //     const options = [...startsWithSearchTerm, ...partialMatches];

// //     this.setState({
// //       options: options
// //     });
// //   }
// // }
